/**
 * Where each artifact's generation stands, for the surfaces that draw it.
 *
 * LOCAL ONLY, on purpose — the canvas's own rule: a generation never writes `status: 'generating'`
 * into the blob, because a crashed tab would leave a slide stuck "writing" forever in a store the
 * other tab trusts. The blob learns exactly once, at commit. Everything in between — the wait, the
 * phase line, the textures the morph needs — lives here, in this tab, and dies with it. A short-lived
 * completed receipt is retained separately so the chat can finish its report after the preview fades.
 *
 * Module store on the `stageSignals` pattern: version counter + reads, `useSyncExternalStore` on the
 * consuming side. The row enrichment reads `state`/`phase`; the preview's stage wrapper reads the
 * whole record to drive the morph.
 */
import type { ArtifactWorkNote } from '~/design-system/components/DeckGen/birtvyOpen/artifactNarration';

/**
 * What a running generation is filed under.
 *
 * A deck artifact is a numeric slide id and always has been. A brand-kit composition is a ROLE
 * ("cover", "section-divider") — the same work, the same phases, the same morph, on a surface where
 * no slide exists to number. The key is widened rather than translated because a fake numeric id for
 * a role would have to be minted, stored and reversed by every surface that touched it, and the two
 * spaces never meet: `Map` compares with SameValueZero, so 42 and '42' are two records, not one.
 */
export type GenKey = number | string;

const listeners = new Set<() => void>();
let version = 0;
const COMPLETED_TTL_MS = 30 * 60 * 1000;

function bump(): void {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribeGen(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function genVersion(): number {
  return version;
}

export function genServerVersion(): number {
  return 0;
}

export interface GenRecord {
  /**
   * `waiting` = the request is out, nothing has streamed; `streaming` = bytes arriving; `finishing`
   * = stream done, validating/committing/baking; `resolving` = the morph is playing its resolve;
   * `failed` = both attempts unusable; `done` is a local receipt retained for the chat after the
   * preview has cleared its active record.
   */
  state: 'waiting' | 'streaming' | 'finishing' | 'resolving' | 'failed' | 'done';
  /** The honest progress line (`describeArtifactProgress`): "Setting the palette", never a percent. */
  phase: string;
  startedAt: number;
  /** Fixed when saving/baking finishes, independent of whether the preview morph is visible. */
  finishedAt?: number;
  title?: string;
  /** Proposed by the generator, published only with the confirmed saved result. */
  summary?: string;
  notes: ArtifactWorkNote[];
  /** Whether the canvas is creating a new artifact or rewriting an existing one. */
  operation: 'edit' | 'generate';
  /** The assistant message that dispatched this work, so receipts stay in the right turn. */
  messageId?: string;
  /** The shared phase queue used by the directory and the chat receipt. */
  steps: GenProgressStep[];
  /** What the artifact looked like before a rewrite — the morph's `from`. Null for a fresh one. */
  from: HTMLCanvasElement | null;
  /** The finished artifact's raster — the morph's `to`. Set exactly when `state` turns `resolving`. */
  to: HTMLCanvasElement | null;
  /** Reading-order delay map derived from `to` (`parts.ts`). */
  parts: { delays: Uint8Array; count: number } | null;
  /** The instruction, so a `failed` board's Try again can re-run it without re-asking. */
  instruction: string;
}

const records = new Map<GenKey, GenRecord>();
/**
 * Chat receipts, and only ever for a numeric key — see `genClear`. The key here is a RECEIPT key
 * (`slideId:messageId`) in its own map, unrelated to a record key, so widening the record key cannot
 * collide with it.
 */
const completed = new Map<string, { slideId: number; record: GenRecord; expiresAt: number }>();

export interface GenProgressStep {
  label: string;
  state: 'done' | 'running' | 'pending';
}

function chatPhaseLabel(phase: string): string {
  return phase === 'Thinking' || phase === 'Writing the artifact' ? 'Preparing the artifact' : phase;
}

function completeSteps(steps: GenProgressStep[]): GenProgressStep[] {
  return steps.map((step) => ({ ...step, state: 'done' }));
}

function advanceSteps(
  steps: GenProgressStep[],
  phase: string,
  state: GenProgressStep['state'] = 'running',
): GenProgressStep[] {
  const label = chatPhaseLabel(phase);
  const last = steps[steps.length - 1];
  if (last?.label === label) {
    return steps.map((step, index) =>
      index === steps.length - 1 ? { ...step, state } : step,
    );
  }
  return [
    ...steps.map((step) => (step.state === 'running' ? { ...step, state: 'done' as const } : step)),
    { label, state },
  ];
}

function pruneCompleted(): void {
  const now = Date.now();
  for (const [key, receipt] of completed) {
    if (receipt.expiresAt <= now) completed.delete(key);
  }
}

export function genRecord(slideId: GenKey): GenRecord | undefined {
  return records.get(slideId);
}

/**
 * A snapshot for surfaces that need the same phase queue as the directory and preview.
 *
 * SLIDES ONLY. Every consumer of this is the chat's turn report, which files an outcome under a slide
 * id and PERSISTS it (`deck15ArtifactReceipts`); a role string reaching that store would be written to
 * localStorage as a slide that does not exist and drawn as an artifact card nobody can open. A
 * surface that keys by string reads its own record with `genRecord(key)`.
 */
export function genRecords(): Array<{ slideId: number; record: GenRecord }> {
  pruneCompleted();
  return [
    ...[...records.entries()].flatMap(([slideId, record]) =>
      typeof slideId === 'number' ? [{ slideId, record }] : [],
    ),
    ...[...completed.values()].map(({ slideId, record }) => ({ slideId, record })),
  ];
}

/** True while any state short of `failed` is active — the "refuse a second generate" latch. */
export function genActive(slideId: GenKey): boolean {
  const record = records.get(slideId);
  return !!record && record.state !== 'failed' && record.state !== 'done';
}

export function genBegin(
  slideId: GenKey,
  instruction: string,
  from: HTMLCanvasElement | null,
  operation: 'edit' | 'generate' = 'generate',
  messageId?: string,
): void {
  for (const [key, receipt] of completed) {
    // A retry reuses the same assistant message; replace only that old failed receipt. A later edit
    // of the same slide belongs to a different turn and must keep its own completed history.
    if (receipt.slideId === slideId && receipt.record.messageId === messageId) completed.delete(key);
  }
  records.set(slideId, {
    state: 'waiting',
    phase: 'Thinking',
    startedAt: Date.now(),
    operation,
    messageId,
    steps: [{ label: 'Preparing the artifact', state: 'running' }],
    from,
    to: null,
    parts: null,
    instruction,
    notes: [],
  });
  bump();
}

export function genPhase(slideId: GenKey, phase: string): void {
  const record = records.get(slideId);
  if (!record || record.state === 'failed' || record.state === 'done') return;
  // The first byte is what flips waiting → streaming; the phase text rides along after.
  const state = record.state === 'waiting' ? 'streaming' : record.state;
  if (record.phase === phase && record.state === state) return;
  records.set(slideId, { ...record, phase, state, steps: advanceSteps(record.steps, phase) });
  bump();
}

export function genFinishing(slideId: GenKey, phase = 'Finishing'): void {
  const record = records.get(slideId);
  if (!record || record.state === 'done') return;
  records.set(slideId, {
    ...record,
    state: 'finishing',
    phase,
    steps: advanceSteps(record.steps, phase),
  });
  bump();
}

/** The source has been saved. Keep the model-authored name for the turn's final receipt. */
export function genCommitted(slideId: GenKey, title?: string, summary?: string): void {
  const record = records.get(slideId);
  if (!record) return;
  records.set(slideId, { ...record, title, summary, state: 'done', finishedAt: Date.now() });
  // Saving the source completes the work. Thumbnail baking is a background cache, not generation.
  genClear(slideId);
}

/** Model-written public commentary and actual host findings, not a timed sequence of stock phrases. */
export function genNarrate(slideId: GenKey, text: string): void {
  const record = records.get(slideId);
  if (!record || record.state === 'failed' || record.state === 'done' || record.state === 'resolving') return;
  const clean = text.trim();
  if (!clean || record.notes.some((note) => note.text === clean) || record.notes.length >= 20) return;
  records.set(slideId, { ...record, notes: [...record.notes, {
    id: `${slideId}-${record.startedAt}-${record.notes.length}`, text: clean, at: Date.now(),
  }] });
  bump();
}

export function genResolve(
  slideId: GenKey,
  to: HTMLCanvasElement | null,
  parts: { delays: Uint8Array; count: number } | null,
): void {
  const record = records.get(slideId);
  if (!record || record.state === 'done') return;
  const steps = advanceSteps(record.steps, 'Applying the finished artifact', 'done');
  records.set(slideId, {
    ...record,
    state: 'resolving',
    finishedAt: Date.now(),
    phase: 'Applying the finished artifact',
    steps,
    to,
    parts,
  });
  bump();
}

export function genFail(slideId: GenKey): void {
  const record = records.get(slideId);
  if (!record || record.state === 'done') return;
  records.set(slideId, { ...record, state: 'failed', finishedAt: Date.now() });
  bump();
}

/** The preview's active record is over; retain a short-lived chat receipt with its phase history. */
export function genClear(slideId: GenKey): void {
  const record = records.get(slideId);
  if (!record) return;
  records.delete(slideId);
  // A receipt exists so the CHAT can finish its report after the preview fades, and only a slide has a
  // chat turn. Banking one for a string key would hold its two 1920x1080 textures for the full TTL on
  // a surface (`genRecords` is slides-only) that can never read it back.
  if (typeof slideId !== 'number') {
    bump();
    return;
  }
  const receiptKey = `${slideId}:${record.messageId ?? record.startedAt}`;
  completed.set(receiptKey, {
    slideId,
    record: {
      ...record,
      state: record.state === 'failed' ? 'failed' : 'done',
      phase: record.operation === 'edit' ? 'Updated the artifact' : 'Wrote the artifact',
      steps: completeSteps(record.steps),
    },
    expiresAt: Date.now() + COMPLETED_TTL_MS,
  });
  bump();
}
